import { accumulatorParameters } from '../../libs/accumulatorEconomics';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  showPrice,
  showDateTime,
  showPercent,
  showStrikePrice,
  showAmount,
} from '../../libs/util';
import CurrencyIcon from '../../components/common/icon';
import { post } from '../../libs/api';

const accumOrderCache = new Map();

const Component = ({ plan, onShow }) => {
  const isPut =
    plan?.product?.option && plan?.product?.option?.option_type == 'PUT';
  const isOption = plan?.product?.product_type === 'OPTION';
  const isAccum = ['ACCUM', 'ACCUM_REV'].includes(plan?.product?.product_type);
  const [accumOrder, setAccumOrder] = useState(() =>
    isAccum && plan?.transaction_id
      ? accumOrderCache.get(plan.transaction_id) || null
      : null
  );

  useEffect(() => {
    if (!isAccum || !plan?.transaction_id) {
      setAccumOrder(null);
      return undefined;
    }

    const cachedOrder = accumOrderCache.get(plan.transaction_id);
    if (cachedOrder) {
      setAccumOrder(cachedOrder);
      return undefined;
    }

    let cancelled = false;
    const loadAccumOrder = async () => {
      const result = await post('/investment/order/get', {
        orderId: plan.transaction_id,
      });
      if (!cancelled && result?.status === 200 && result?.data) {
        accumOrderCache.set(plan.transaction_id, result.data);
        setAccumOrder(result.data);
      }
    };

    loadAccumOrder();

    return () => {
      cancelled = true;
    };
  }, [isAccum, plan?.transaction_id]);

  const getCustomEbtcaProduct = accumulatorParameters;
  const getAccumNotionalCcy = (source) => source?.notional_amount_ccy || source?.invest_ccy || '';

  const getTitle = () => {
    const titleSource = isAccum ? accumOrder || plan : plan;
    let title = titleSource?.product?.description || '';
    if (title.includes('DCI')) {
      title = title.replace(/USD-[CP]/, 'USDC');
    } else if (isAccum) {
      const customEbtcaProduct = getCustomEbtcaProduct(titleSource);
      const rawFrequency = String(
        customEbtcaProduct?.frequency ||
          titleSource?.monthly_or_weekly ||
          ''
      ).trim();
      const periodLabel = rawFrequency.toUpperCase().includes('MONTH')
        ? 'Months'
        : rawFrequency.toUpperCase().includes('WEEK')
          ? 'Weeks'
          : rawFrequency;
      const noFixings =
        customEbtcaProduct?.no_fixings ||
        titleSource?.no_fixings ||
        (accumOrder ? titleSource?.tenor : '') ||
        '';
      const ccy = getAccumNotionalCcy(titleSource);
      title = ['Accumulator', noFixings, periodLabel, ccy]
        .filter(Boolean)
        .join(' ');
    }
    if (!isAccum && plan?.option_list) {
      const quoteCurrency = plan.option_list[0].quoteCurrency;
      title = `${plan?.product?.description} - (with ${quoteCurrency} Option)`;
    }

    if (isAccum) {
      title = title.replace(/USTD/g, 'USDT');
    }
    return title;
  };

  // PB-2339: bond holdings link to their master data page.
  const isBond =
    plan?.asset_category === 'BON' && plan?.securitymaster_id != null;
  const bondLink = isBond && (
    <div className="nk-odr-meta">
      <Link to={'/portal/bonds/' + plan.securitymaster_id}>
        Bond details <em className="icon ni ni-arrow-long-right"></em>
      </Link>
    </div>
  );

  if (plan?.status != 'COMPLETED') {
    return (
      <div className="nk-odr-item">
        <div className="nk-odr-col">
          <div className="nk-odr-info">
            <div className="nk-odr-badge">
              {isPut ? (
                <CurrencyIcon
                  currency={plan?.product?.option?.quote_currency.toLowerCase()}
                />
              ) : (
                <CurrencyIcon currency="USDC" />
              )}
            </div>
            <div className="nk-odr-data">
              <div className="nk-odr-label">{getTitle()}</div>
              <div className="nk-odr-meta">
                <span className="date">{plan?.reference_number}</span>
              </div>
              {bondLink}
              <div className="nk-odr-meta">
                <span className="date">Invested:</span>
                <span className="date">
                  {plan?.term_expired_amount_ccy === 'USD'
                    ? showPrice(
                        plan?.term_expired_amount,
                        plan?.term_expired_amount_ccy
                      ) +
                      ' (' +
                      showPrice(
                        plan?.contributionAmount,
                        plan?.contributionAmountCcy
                      ) +
                      ')'
                    : showAmount(plan?.invest_amount, null, 8, false) +
                      ' ' +
                      plan?.invest_ccy}
                </span>
                <span
                  className={
                    plan?.order_status != 'LAPSED' &&
                    plan?.order_status != 'EXERCISED'
                      ? 'status text-dark'
                      : 'status text-success'
                  }
                >
                  {plan?.order_status}
                </span>
              </div>
              <div className="nk-odr-meta">
                <span className="date">APR:</span>
                <span className="date">{showPercent(plan?.term_apy)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-odr-col nk-odr-col-amount">
          <div className="nk-odr-label">
            {showDateTime(plan?.value_datetime)}
          </div>
          <div className="nk-odr-meta">Invest Date</div>
        </div>
        <div className="nk-odr-col nk-odr-col-amount">
          <div className="nk-odr-label">
            {showDateTime(plan?.maturity_datetime)}
          </div>
          <div className="nk-odr-meta">Maturity Date</div>
        </div>

        <div className="nk-odr-col nk-odr-col-amount text-center">
          {isOption && (
            <div className="nk-odr-meta">
              if Reference Rate{' '}
              {isPut ? String.fromCharCode(8805) : String.fromCharCode(8804)}{' '}
              {showStrikePrice(plan?.product?.option?.strike_price, 'USD')}
            </div>
          )}
          <div className="text-center">
            <strong className="text-dark">
              {showPrice(
                plan?.term_expired_amount,
                plan?.term_expired_amount_ccy
              )}
            </strong>
          </div>
          <div className="nk-odr-meta">Return</div>
        </div>

        {plan?.term_exercised_amount && (
          <div className="nk-odr-col nk-odr-col-amount text-center">
            {isOption && (
              <div className="nk-odr-meta">
                if Reference Rate{' '}
                {isPut ? String.fromCharCode(60) : String.fromCharCode(62)}{' '}
                {showStrikePrice(plan?.product?.option?.strike_price, 'USD')}
              </div>
            )}
            <div className="">
              <strong className="text-dark">
                {showPrice(
                  plan?.term_exercised_amount,
                  plan?.term_exercised_amount_ccy
                )}
              </strong>
            </div>
            <div className="nk-odr-meta">Return</div>
          </div>
        )}

        <div className="nk-odr-col nk-odr-col-action">
          <div className="nk-odr-action">
            <Link
              className="tnx-details"
              to={'/portal/investment/order/' + plan.transaction_id}
            >
              <em className="icon ni ni-forward-ios"></em>
            </Link>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="nk-odr-item">
        <div className="nk-odr-col">
          <div className="nk-odr-info">
            <div className="nk-odr-badge">
              {isPut ? (
                <CurrencyIcon
                  currency={plan?.product?.option?.quote_currency}
                />
              ) : (
                <CurrencyIcon currency="USDC" />
              )}
            </div>
            <div className="nk-odr-data">
              <div className="nk-odr-label">
                {isAccum ? getTitle() : plan?.product?.description}
              </div>
              <div className="nk-odr-meta">
                <span className="date">{plan?.reference_number}</span>
              </div>
              {bondLink}
              <div className="nk-odr-meta">
                <span className="date">Invested:</span>
                <span className="date">
                  {plan?.term_expired_amount_ccy === 'USD'
                    ? showPrice(
                        plan?.term_expired_amount,
                        plan?.term_expired_amount_ccy
                      ) +
                      ' (' +
                      showPrice(
                        plan?.contributionAmount,
                        plan?.contributionAmountCcy
                      ) +
                      ')'
                    : showPrice(plan?.invest_amount, plan?.invest_ccy)}
                </span>
                <span className="status text-danger">{plan?.order_status}</span>
              </div>
              <div className="nk-odr-meta">
                <span className="date">APR:</span>
                <span className="date">{showPercent(plan?.term_apy)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-odr-col nk-odr-col-amount">
          <div className="nk-odr-label">
            {showDateTime(plan?.value_datetime)}
          </div>
          <div className="nk-odr-meta">Invest Date</div>
        </div>
        <div className="nk-odr-col nk-odr-col-amount">
          <div className="nk-odr-label">
            {showDateTime(plan?.maturity_datetime)}
          </div>
          <div className="nk-odr-meta">Maturity Date</div>
        </div>

        <div className="nk-odr-col nk-odr-col-amount">
          <div className="nk-odr-label">
            {showDateTime(plan?.redemption_datetime)}
          </div>
          <div className="nk-odr-meta">Redemption Date</div>
        </div>

        <div className="nk-odr-col nk-odr-col-amount text-center">
          <div className="nk-odr-meta">Return</div>
          <div className="">
            <strong className="text-dark">
              {showPrice(plan?.redeem_amount, plan?.redeem_amount_ccy)}
            </strong>
          </div>
        </div>

        <div className="nk-odr-col nk-odr-col-action">
          <div className="nk-odr-action">
            <Link
              className="tnx-details"
              to={'/portal/investment/order/' + plan.transaction_id}
            >
              <em className="icon ni ni-forward-ios"></em>
            </Link>
          </div>
        </div>
      </div>
    );
  }
};

export default Component;
